/* CDT Studio — conflict auto-resolution.  Browser bundle: window.CDTResolve
   Generated from plan.js / space.js / grade.js / audit.js / ops.js / resolve.js / index.js.
   Edit those and re-run `node bundle.js`, not this file. */
;(function (root) {
'use strict';
/* ─── plan.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   plan.js — the normalised planogram model every operation works on.

   Positions live in inches along a run. A run is BAYS bays wide; a lane is one
   (run, shelf) pair and is the only thing a facing can overlap in. Everything
   downstream — auditing, harvesting, repacking — reads and writes this model
   and nothing else, so an operation can be tested without a DOM.
   ──────────────────────────────────────────────────────────────────────────── */

const BAY_W = 121.92;
const BAYS = 11;
const SHELVES = 6;
const RUN_W = BAY_W * BAYS;              // 1341.12 in of linear shelf per lane

/* The as-built plan carries ±0.2 in of rounding jitter between neighbours, so
   "touching" has to be a tolerance, not an equality. Anything under ADJ is the
   same stretch; anything under OVER is not a real overlap. */
const EPS = 0.05;
const ADJ = 1.0;
const OVER = 0.3;

const SEP = ' › ';                  // the " › " the app uses in node keys

/* ── which way is up ───────────────────────────────────────────────────────
   SHELF RANK 1 IS THE TOP SHELF. Rank rises as you go down the bay, so rank 6 is
   the bottom. This is not a guess and it is not arbitrary — it comes from the
   studio's own painter, which puts a shelf rail at

       rail[rank] = (fixtureHeight - geo.y - 1.2) * k

   i.e. it flips geo.y, so geo.y is height above the floor and the *largest* y
   (rank 1) is the highest shelf. The pin bands agree: their top edge is
   base[shelfMin - 1] - pitch[shelfMin - 1], which only reads as "top" if the
   smaller rank is higher up.

   Every vertical decision in this codebase goes through the two helpers below
   rather than comparing rank numbers inline, because getting this backwards is
   silent and expensive: it inverts the whole size gradient and hangs the jumbo
   bottles over the shopper's head. */
const isAbove = (shelfA, shelfB) => shelfA < shelfB;      // lower rank = higher up
const topFirst = (a, b) => a.shelf - b.shelf;             // ascending rank = top down
const bottomFirst = (a, b) => b.shelf - a.shelf;

/* ── size labels ───────────────────────────────────────────────────────────
   "146.0 fl oz", "16 ct", "70 loads", "3 lb", "2.7 oz". Size order is only
   meaningful inside a unit family, so the key is (family, magnitude) and
   families keep a stable order of their own. */
const UNIT_FAMILY = [
  [/fl\s*oz/i, 'volume', 1],
  [/\bml\b/i, 'volume', 0.033814],
  [/\bl\b/i, 'volume', 33.814],
  [/\blb\b|pound/i, 'mass', 16],
  [/\boz\b/i, 'mass', 1],
  [/\bg\b/i, 'mass', 0.035274],
  [/\bct\b|count|\bpk\b|pack/i, 'count', 1],
  [/load/i, 'loads', 1],
];
const FAMILY_ORDER = ['count', 'loads', 'mass', 'volume', 'other'];

function parseSize(label) {
  if (label == null) return { family: 'other', mag: 0, raw: '' };
  const s = String(label);
  const m = s.match(/-?\d+(?:\.\d+)?/);
  const n = m ? parseFloat(m[0]) : 0;
  for (const [re, family, mult] of UNIT_FAMILY) {
    if (re.test(s)) return { family, mag: n * mult, raw: s };
  }
  return { family: 'other', mag: n, raw: s };
}

function sizeKey(label) {
  const p = parseSize(label);
  const f = FAMILY_ORDER.indexOf(p.family);
  return [(f < 0 ? FAMILY_ORDER.length : f), p.mag];
}

/* ── the model ─────────────────────────────────────────────────────────────
   A placement is one SKU's block of facings on one lane:
     id, run, shelf, x (left edge), w (single-facing width), hf (facings),
     h (pack height), path (its value at every CDT level), node (depth-2 key).
   width() is w*hf — the linear inches the SKU actually consumes. */

class Plan {
  constructor(spec) {
    this.spine = spec.spine.slice();
    this.orient = Object.assign({}, spec.orient);
    this.order = {};
    Object.keys(spec.order || {}).forEach((k) => (this.order[k] = spec.order[k].slice()));
    this.geo = spec.geo || [];
    this.runW = spec.runW == null ? RUN_W : spec.runW;
    this.sizeRule = spec.sizeRule || 'ascending';
    this.items = new Map();               // id -> placement
    this.log = [];
    (spec.items || []).forEach((it) => this.items.set(it.id, Object.assign({}, it)));
    this._rank = {};                      // attr -> value -> index, memoised
  }

  clone() {
    const p = new Plan({
      spine: this.spine, orient: this.orient, order: this.order,
      geo: this.geo, runW: this.runW, sizeRule: this.sizeRule, items: [],
    });
    this.items.forEach((v, k) => p.items.set(k, Object.assign({}, v)));
    p.log = this.log.slice();
    return p;
  }

  get(id) { return this.items.get(id); }
  all() { return [...this.items.values()]; }

  static width(it) { return it.w * it.hf; }
  static right(it) { return it.x + it.w * it.hf; }

  /* every placement on one lane, left to right */
  lane(run, shelf) {
    const out = [];
    this.items.forEach((it) => { if (it.run === run && it.shelf === shelf) out.push(it); });
    out.sort((a, b) => a.x - b.x || a.id.localeCompare(b.id));
    return out;
  }

  lanes() {
    const seen = new Map();
    this.items.forEach((it) => {
      const k = it.run + '|' + it.shelf;
      if (!seen.has(k)) seen.set(k, { run: it.run, shelf: it.shelf });
    });
    return [...seen.values()].sort((a, b) => a.run - b.run || topFirst(a, b));
  }

  /* usable shelf height (pack must fit under the shelf above) */
  pitch(run, shelf) {
    const g = this.geo[run];
    if (!g || !g.shelves) return Infinity;
    const s = g.shelves.find((z) => z.rank === shelf);
    return s ? s.pitch : Infinity;
  }

  fitsShelf(it, run, shelf) { return it.h <= this.pitch(run, shelf) + EPS; }

  /* ── CDT ordering ─────────────────────────────────────────────────────── */
  rank(attr, value) {
    let m = this._rank[attr];
    if (!m) {
      m = this._rank[attr] = new Map();
      (this.order[attr] || []).forEach((v, i) => m.set(v, i));
    }
    const r = m.get(value);
    return r === undefined ? 1e6 : r;
  }

  /* ── the one true target order ───────────────────────────────────────────
     Sort key for one item along the whole spine, read straight off the tree:
     spine gives the levels, `order` (the app's `vorder`) gives each level's value
     sequence, and that is the same pair `buildLayout(ITEMS, spine, vorder, ORIENT)`
     builds from. One order per level, applied tree-wide.

     This is worth stating plainly because it is the thing that makes the whole
     algorithm coherent. Every fix — a size order in Tide, an HE order five levels
     down, a flavour order six levels down — is driven by this single key, so no
     two defects can ask for conflicting arrangements and the outcome cannot depend
     on which pin you click. An earlier version derived each fix's target from that
     pin's own `seqOrder` blob instead; because those blobs are per-defect
     permutations, two pins in the same branch could disagree about a level neither
     of them owned, and each fix silently undid the other. There is no such thing
     as a branch-local order here, and the fix was to stop inventing one.

     Size is the one level read as a rule rather than a list: "small → large"
     within a unit family, which is what the size-order defects' own fixes state
     ("size order pushed to the level"). Pass sizeRule:'listed' to honour
     order.__sizeLabel literally instead. */
  cdtKey(it) {
    const key = [];
    this.spine.forEach((attr, d) => {
      const v = it.path[d];
      if (attr === '__sizeLabel' && this.sizeRule !== 'listed') {
        const sk = sizeKey(v);
        key.push(sk[0], sk[1]);
      } else key.push(this.rank(attr, v), String(v == null ? '' : v));
    });
    return key;
  }

  cdtCompare(a, b) {
    const ka = this.cdtKey(a), kb = this.cdtKey(b);
    for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
      const x = ka[i], y = kb[i];
      if (x === y) continue;
      if (typeof x === 'number' && typeof y === 'number') return x - y;
      return String(x) < String(y) ? -1 : 1;
    }
    return 0;
  }

  nodeKey(it, depth) { return it.path.slice(0, depth).join(SEP); }
}

/* ── load from the app's baked JSON ───────────────────────────────────────── */
function loadPlan(canvas, itemsDoc, opts) {
  const attrs = new Map();
  itemsDoc.ITEMS.forEach((r) => attrs.set(r.id, r.a));
  const spine = itemsDoc.SPINE;
  const items = [];
  canvas.runs.forEach((r) => r.items.forEach((it) => {
    const a = attrs.get(it.id) || {};
    items.push({
      id: it.id, run: r.run, shelf: it.shelf, x: it.x,
      w: it.w, hf: it.hf, h: it.h, hf0: it.hf, ix: it.ix,
      path: spine.map((s) => a[s]),
      node: it.node, sec: it.sec, brand: it.brand, desc: it.desc,
    });
  }));
  const plan = new Plan(Object.assign({
    spine, orient: itemsDoc.ORIENT, order: itemsDoc.ORDER,
    geo: canvas.geo, runW: (canvas.bayW || BAY_W) * BAYS, items,
  }, opts || {}));

  /* Put the size level's own list in the order the rule implies, so anything else
     reading vorder — the app's rebuild included — agrees with what we lay out.
     This is exactly what the size-order fixes describe themselves as doing. */
  if (plan.sizeRule !== 'listed' && plan.order.__sizeLabel) {
    plan.order.__sizeLabel = plan.order.__sizeLabel.slice().sort((a, b) => {
      const x = sizeKey(a), y = sizeKey(b);
      return x[0] - y[0] || x[1] - y[1] || (String(a) < String(b) ? -1 : 1);
    });
    plan._rank = {};
  }
  return plan;
}


/* ─── space.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   space.js — the four primitives every resolution is built from.

   The shelves in this plan are ~99% full: summed facing width per lane is within
   a couple of inches of the 1341 in the lane has. So "move a facing here" is
   never a placement problem, it is a *space* problem, and the only currency the
   base plan is allowed to spend is facings. Hence:

     intervals()   where a block physically is, per lane
     repack()      re-lay a set of SKUs into a fixed interval, choosing facings
                   so the interval is filled exactly — this is both the layout
                   engine and the harvester, because it derives facings from
                   scratch (1 up) instead of preserving them
     fillHole()    a facing left; grow its lane neighbours into the gap so the
                   surrounding blocks stay solid instead of leaving a void
     grow()        widen an interval by taking facings off its lane neighbours
                   and sliding them outward, for when repack() cannot fit

   repack() is deliberately the only thing that writes x. Everything else asks
   it to.
   ──────────────────────────────────────────────────────────────────────────── */


/* ── where a set of SKUs physically sits, as per-lane intervals ─────────────
   A block's footprint on a lane is the space it *owns*: two of its facings belong
   to the same stretch unless somebody else's facing sits between them. Merging
   on a distance tolerance instead was subtly wrong — a repack that left an inch
   of whitespace could split one stretch into two, which changed the footprint,
   which changed the layout, which moved the whitespace: pressing Auto-resolve
   twice never settled. Ownership does not wobble; distance does.

   Reclaiming the whitespace is also the right answer on its own terms: it is the
   block's own space, so the block should use it. */
function intervals(plan, ids) {
  const want = new Set(ids);
  const byLane = new Map();
  ids.forEach((id) => {
    const it = plan.get(id);
    if (!it || it.run < 0) return;
    const k = it.run + '|' + it.shelf;
    if (!byLane.has(k)) byLane.set(k, { run: it.run, shelf: it.shelf });
  });
  const out = [];
  byLane.forEach(({ run, shelf }) => {
    const lane = plan.lane(run, shelf);
    let cur = null;
    lane.forEach((it) => {
      if (want.has(it.id)) {
        if (cur) { cur.x1 = Math.max(cur.x1, Plan.right(it)); cur.ids.push(it.id); }
        else { cur = { run, shelf, x0: it.x, x1: Plan.right(it), ids: [it.id] }; out.push(cur); }
      } else if (cur) {
        cur = null;                       // a foreign facing ends the stretch
      }
    });
  });
  out.sort((a, b) => a.run - b.run || topFirst(a, b) || a.x0 - b.x0);
  out.forEach((iv) => (iv.cap = iv.x1 - iv.x0));
  return out;
}

/* ── reading order over intervals ──────────────────────────────────────────
   A block's own stretches, in the order a sequence is laid into them: top shelf
   first, left to right along each shelf. Shelf 1 is the top shelf.

   This deliberately depends only on which lane a facing is on, never on how the
   x-ranges happen to cluster. An earlier version grouped the stretches into
   x-columns first, which was arguably a better read of a horizontally-split
   level — but the clustering shifted as soon as a repack moved anything by an
   inch, so "put the block in order" and "is the block in order?" could disagree
   about the same plan. A rule the layout and the audit can never read
   differently is worth more here than a cleverer one. */
function orderIntervals(plan, ivs, _orientation) {
  return ivs.slice().sort((a, b) => a.run - b.run || topFirst(a, b) || a.x0 - b.x0);
}

/* ── the facing budget ─────────────────────────────────────────────────────
   Lay `seq` (ordered SKU ids) into [x0, x0+cap] on one lane, choosing hf per
   SKU so the interval is filled as completely as whole facings allow.

   Every SKU starts at one facing — that is the minimum a planogram may carry
   and the reason space can always be found for a relocated SKU. Leftover width
   is then handed back: first to SKUs that had extra facings before (so the plan
   keeps the shape the space planner gave it), then, if width is still spare,
   round-robin so the interval closes up rather than showing a gap.

   Returns null if even one facing each does not fit — the caller must grow(). */
function fitFacings(plan, seq, cap, opts) {
  const o = opts || {};
  const items = seq.map((id) => plan.get(id));
  const base = items.reduce((s, it) => s + it.w, 0);
  if (base > cap + OVER) return null;

  const hf = items.map(() => 1);
  let used = base;

  /* pass 1 — hand back the facings the plan was authored with, biggest claim
     first so nobody is starved. Anchoring on the *authored* count rather than the
     current one is what makes this a pure function of (sequence, interval): press
     Auto-resolve twice and the second press is a no-op. */
  const want = items.map((it, i) => ({ i, want: Math.max(1, o.keepFacings === false ? 1 : (it.hf0 || it.hf)) }));
  want.sort((a, b) => (b.want - hf[b.i]) - (a.want - hf[a.i]));
  let moved = true;
  while (moved) {
    moved = false;
    for (const { i, want: wnt } of want) {
      if (hf[i] < wnt && used + items[i].w <= cap + EPS) { hf[i]++; used += items[i].w; moved = true; }
    }
  }

  // pass 2 — spend the remainder so the block is solid, narrowest SKU first
  // (a narrow SKU wastes the least and lets us land closest to an exact fit)
  const ceiling = o.maxFacings == null ? 12 : o.maxFacings;
  const narrow = items.map((it, i) => i).sort((a, b) => items[a].w - items[b].w);
  moved = true;
  while (moved) {
    moved = false;
    for (const i of narrow) {
      if (hf[i] < ceiling && used + items[i].w <= cap + EPS) { hf[i]++; used += items[i].w; moved = true; }
    }
  }
  return { hf, used, slack: cap - used };
}

/* Write a sequence into an interval. Mutates the plan.

   Whatever whole facings cannot absorb — always less than one facing of the
   narrowest SKU present — is spread evenly between the facings instead of being
   left as one void at the end. That matters far more than it looks: it means an
   interval always ends exactly where it started, so no facing outside the block
   ever has to move, and a fix can never ripple into a neighbouring block's
   alignment. A 13 in void at the end of a block reads as a hole on the shelf; the
   same 13 in spread over nine facings is invisible. */
function repack(plan, iv, seq, opts) {
  const o = opts || {};
  const fit = fitFacings(plan, seq, iv.cap, opts);
  if (!fit) return { ok: false, need: seq.reduce((s, id) => s + plan.get(id).w, 0) - iv.cap };
  const slack = Math.max(0, fit.slack);
  const spread = (o.spread === false || seq.length < 2) ? 0 : slack / (seq.length - 1);
  let cx = iv.x0;
  seq.forEach((id, i) => {
    const it = plan.get(id);
    it.run = iv.run; it.shelf = iv.shelf; it.hf = fit.hf[i];
    it.x = +cx.toFixed(2);
    cx += it.w * it.hf + (i < seq.length - 1 ? spread : 0);
  });
  return { ok: true, slack: +slack.toFixed(2), spread: +spread.toFixed(2), end: +cx.toFixed(2) };
}

/* ── close a lane up, preserving order ─────────────────────────────────────
   Used after a removal. Facings keep their relative order and their block
   membership, so no contiguity can break; only x changes. `from` is the x at
   which shifting starts — everything left of it is untouched. */
function closeUp(plan, run, shelf, from) {
  const arr = plan.lane(run, shelf);
  let cx = null;
  arr.forEach((it) => {
    if (it.x + EPS < from) { cx = Math.max(cx == null ? -Infinity : cx, Plan.right(it)); return; }
    if (cx == null) cx = it.x;
    it.x = +Math.max(0, cx).toFixed(2);
    cx = Plan.right(it);
  });
}

/* ── repair the vacated site ───────────────────────────────────────────────
   A SKU has been lifted out of a lane, leaving `hole` inches between `left` and
   `right`. Leaving a void is not an option — the base plan is meant to read as
   solid blocking — so the neighbours grow into it.

   Preference order, because it is what keeps blocking intact:
     1. a neighbour on the same node as the other neighbour (the hole was inside
        someone's block: that block simply gets wider)
     2. otherwise both neighbours, alternating, so the seam stays where it was
     3. whatever the hole cannot absorb in whole facings gets closed up */
function fillHole(plan, run, shelf, x0, hole, opts) {
  const o = opts || {};
  const arr = plan.lane(run, shelf);
  const left = arr.filter((it) => Plan.right(it) <= x0 + ADJ).pop() || null;
  const right = arr.find((it) => it.x >= x0 + hole - ADJ) || null;
  if (!left && !right) return { absorbed: 0, residual: +hole.toFixed(2), grown: [] };

  /* The window is the two stretches either side of the hole. Its outer edges are
     fixed *before* anything grows — that is the whole trick. Everything inside is
     re-laid to fill exactly that span, so no facing beyond the window ever moves
     and no other block's alignment can change. Anything whole facings cannot
     absorb is spread across the window's facings rather than left as a void. */
  const nodeOf = (it) => plan.nodeKey(it, 2);
  const li = left ? arr.indexOf(left) : -1;
  const ri = right ? arr.indexOf(right) : -1;
  const win = [];
  if (left) {
    let i = li;
    while (i >= 0 && nodeOf(arr[i]) === nodeOf(left)
      && (i === li || Plan.right(arr[i]) >= arr[i + 1].x - ADJ)) { win.unshift(arr[i]); i--; }
  }
  if (right) {
    let i = ri;
    while (i < arr.length && nodeOf(arr[i]) === nodeOf(right)
      && (i === ri || arr[i].x <= Plan.right(arr[i - 1]) + ADJ)) { win.push(arr[i]); i++; }
  }
  /* If the window is only a couple of facings wide, whatever is left over after
     whole facings shows up as one visible gap. Widen the window by whole
     neighbouring stretches until there are enough facings to hide the residue in
     — block boundaries drift a fraction of an inch, no facing changes block. */
  const MIN_WIN = 6;
  while (win.length < MIN_WIN) {
    const lo = arr.indexOf(win[0]), hi = arr.indexOf(win[win.length - 1]);
    if (lo <= 0 && hi >= arr.length - 1) break;
    if (lo > 0) {
      const k = nodeOf(arr[lo - 1]);
      let i = lo - 1;
      while (i >= 0 && nodeOf(arr[i]) === k) { win.unshift(arr[i]); i--; }
    } else if (hi < arr.length - 1) {
      const k = nodeOf(arr[hi + 1]);
      let i = hi + 1;
      while (i < arr.length && nodeOf(arr[i]) === k) { win.push(arr[i]); i++; }
    }
  }

  const a = win[0].x;
  const b = (right || arr.indexOf(win[win.length - 1]) > ri)
    ? Plan.right(win[win.length - 1]) : x0 + hole;
  const cap = b - a;

  const cands = [];
  if (left && right && nodeOf(left) === nodeOf(right)) cands.push(left, right);
  else { if (left) cands.push(left); if (right) cands.push(right); }
  win.forEach((it) => { if (cands.indexOf(it) < 0) cands.push(it); });   // then the rest of the window

  const grown = [];
  const ceiling = o.maxFacings == null ? 12 : o.maxFacings;
  let used = win.reduce((s, it) => s + Plan.width(it), 0);
  let progress = true;
  while (progress && cap - used > EPS) {
    progress = false;
    for (const c of cands) {
      if (c.hf < ceiling && used + c.w <= cap + EPS) {
        c.hf++; used += c.w; progress = true; grown.push(c.id);
        if (cap - used <= EPS) break;
      }
    }
  }
  const rest = Math.max(0, cap - used);
  repack(plan, { run, shelf, x0: a, x1: b, cap }, win.map((s) => s.id), o);

  return { absorbed: +(hole - rest).toFixed(2), residual: +rest.toFixed(2), grown };
}

/* ── widen an interval by borrowing facings from its lane neighbours ───────
   Only reached when a SKU genuinely will not fit at one facing each inside the
   destination's own footprint. Nearest neighbours give up facings first, and
   only ever down to one, so no SKU is ever delisted to make room. */
function grow(plan, iv, need, opts) {
  const o = opts || {};
  const own = new Set(iv.ids);
  const arr = plan.lane(iv.run, iv.shelf).filter((it) => !own.has(it.id));
  const rightSide = arr.filter((it) => it.x >= iv.x1 - ADJ).sort((a, b) => a.x - b.x);
  const leftSide = arr.filter((it) => Plan.right(it) <= iv.x0 + ADJ).sort((a, b) => a.x - b.x);
  const inside = arr.filter((it) => rightSide.indexOf(it) < 0 && leftSide.indexOf(it) < 0);
  if (inside.length) return { ok: false, reason: 'foreign facings inside the interval' };

  const laneEnd = arr.length ? Math.max(...arr.map(Plan.right)) : iv.x1;
  let budget = Math.max(0, plan.runW - Math.max(laneEnd, iv.x1));   // slack already on the lane
  const took = [];

  // Take facings from the right first — sliding the right-hand blocks outward is
  // the smallest disturbance available. Nearest neighbour pays first.
  const drain = (list) => {
    let progress = true;
    while (budget < need - EPS && progress) {
      progress = false;
      for (const it of list) {
        if (it.hf > 1) { it.hf--; budget += it.w; took.push(it.id); progress = true; }
        if (budget >= need - EPS) break;
      }
    }
  };
  drain(rightSide);
  const fromRight = budget;
  if (budget < need - EPS) drain(leftSide);
  const fromLeft = budget - fromRight;

  if (budget < need - OVER) {
    return { ok: false, reason: 'lane has no facings left to give', freed: +budget.toFixed(2), need: +need.toFixed(2) };
  }

  const take = Math.min(need, budget);
  // grow leftward only by as much as the left side actually gave up
  const leftShare = Math.min(take, Math.max(0, fromLeft));
  const x0 = +(iv.x0 - leftShare).toFixed(2);
  const newCap = iv.cap + take;

  if (leftShare > 0) {                      // re-lay the left blocks, order intact
    let cx = leftSide.length ? leftSide[0].x : x0;
    leftSide.forEach((it) => { it.x = +cx.toFixed(2); cx += Plan.width(it); });
  }
  let rx = x0 + newCap;
  rightSide.forEach((it) => { it.x = +rx.toFixed(2); rx += Plan.width(it); });

  return {
    ok: true,
    iv: { run: iv.run, shelf: iv.shelf, x0, x1: +(x0 + newCap).toFixed(2), cap: newCap, ids: iv.ids },
    freed: +budget.toFixed(2), took, laneEnd: +rx.toFixed(2),
  };
}


/* ─── grade.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   grade.js — the vertical rule: small and light up, big and heavy down.

   The CDT says what order a block runs in. It does not say which shelf anything
   goes on — that is a physical rule about reach and weight, and it is absolute:
   a shopper should not have a 164 fl oz jug of fabric conditioner over their
   head, and a 9 in dryer-sheet carton should not be at ankle height.

   The as-built plan already follows it, and follows it well: in aisle 1 the median
   pack runs 22in / 42 fl oz on the top shelf down to 34in / 154 fl oz on the
   bottom, and aisle 2 does the same. So this module is a *guard*, not a rebuild —
   96 inverted pairs across 18 blocks to tidy, not a plan to turn over.

   That distinction cost a full rewrite to learn. An earlier version read shelf
   rank the wrong way round, scored this correct plan at 2323 inversions, and
   "fixed" it by hoisting every jumbo bottle to the top shelf. Which way is up is
   defined once, in plan.js, from the studio's own painter — never inferred here.

   The vertical key follows the CDT's own size level, so grading and sequencing
   agree rather than fighting: within a block the same "small → large" that runs
   left to right also runs top to bottom. Unit families are ranked before
   magnitudes (counts, then loads, then weights, then volumes) because "180 ct"
   and "24 fl oz" are not comparable numbers — and because that ordering puts the
   light soft packs above the liquids, which is the rule anyway.
   ──────────────────────────────────────────────────────────────────────────── */


const SIZE_ATTR = '__sizeLabel';
const FAM = 1e9;                    // family dominates magnitude in the scalar key

/* One comparable number per SKU: bigger means lower down the bay.

   A label that carries no usable magnitude ("size not stated") is imputed from
   the pack's own face area, matched to the nearest labelled SKU in the same
   block — better than dropping it to one end of the block on a technicality. */
function verticalKeys(plan, ids) {
  const d = plan.spine.indexOf(SIZE_ATTR);
  const rows = ids.map((id) => plan.get(id)).filter(Boolean).map((it) => ({
    it, k: d < 0 ? [4, 0] : sizeKey(it.path[d]), area: it.w * it.h,
  }));
  const labelled = rows.filter((r) => r.k[1] > 0);
  const out = new Map();
  rows.forEach((r) => {
    if (r.k[1] > 0) { out.set(r.it.id, r.k[0] * FAM + r.k[1]); return; }
    const near = labelled.slice()
      .sort((a, b) => Math.abs(a.area - r.area) - Math.abs(b.area - r.area))[0];
    out.set(r.it.id, near ? near.k[0] * FAM + near.k[1] : r.area);
  });
  return out;
}

/* The blocks the size level grades inside: everything above size in the spine —
   here Subcategory / Brand / Form / Handle. That is the unit the CDT hands to
   the size level, so it is the unit the gradient applies to. */
function blocksAt(plan) {
  const d = Math.max(1, plan.spine.indexOf(SIZE_ATTR));
  const out = new Map();
  plan.all().forEach((it) => {
    const k = it.path.slice(0, d).join(SEP);
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(it.id);
  });
  return out;
}

/* How badly a block breaks the rule: pairs where the higher facing is the bigger
   pack. `forced` counts the ones the fixture makes unavoidable — the pack is too
   tall for the shelf it would have to drop to — so they can be excluded from the
   verdict instead of holding a block open forever. */
function inversions(plan, ids) {
  const vk = verticalKeys(plan, ids);
  const its = ids.map((id) => plan.get(id)).filter(Boolean);
  let count = 0, forced = 0;
  for (const a of its) {
    for (const b of its) {
      if (a.run !== b.run || !isAbove(a.shelf, b.shelf)) continue;   // a must be higher up
      if (vk.get(a.id) <= vk.get(b.id)) continue;                    // and the bigger pack
      count++;
      /* Forced is judged pair by pair: the two cannot trade places if the bigger
         pack does not fit under the smaller one's shelf. Measuring it against the
         block's lowest occupied shelf instead was self-defeating — grading changes
         which shelves a block occupies, so the denominator moved underneath the
         measurement and a block that had just been improved scored worse. */
      if (!plan.fitsShelf(a, a.run, b.shelf)) forced++;
    }
  }
  return { count, forced, avoidable: count - forced };
}

/* Every block, summed — the number that has to go down, not up, after a fix. */
function totalInversions(plan) {
  let count = 0, forced = 0, blocks = 0;
  blocksAt(plan).forEach((ids) => {
    const its = ids.map((id) => plan.get(id));
    if (new Set(its.map((i) => i.run + '|' + i.shelf)).size < 2) return;
    const r = inversions(plan, ids);
    count += r.count; forced += r.forced;
    if (r.avoidable > 0) blocks++;
  });
  return { count, forced, avoidable: count - forced, blocks };
}


/* ─── audit.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   audit.js — everything that says "this plan is / is not legal".

   The resolution algorithm is only trustworthy if the definition of "resolved"
   is mechanical. Three families of check:

     physical   overlap, lane over-run, pack taller than the shelf pitch
     blocking   a node's facings interrupted by a foreign facing on a lane,
                and a node holding more than one stretch across the run
     sequence   a block's facings out of CDT order in its reading direction

   Physical checks are absolute — a fix that creates one is rejected. Blocking
   and sequence checks are graded against a baseline, because the as-built plan
   already fails many of them (that is what the studio exists to show); a fix is
   only rejected if it makes a *new* one.
   ──────────────────────────────────────────────────────────────────────────── */


/* ── physical ─────────────────────────────────────────────────────────────── */

function physical(plan) {
  const overlaps = [], overrun = [], tooTall = [], badFacings = [];
  plan.lanes().forEach(({ run, shelf }) => {
    const arr = plan.lane(run, shelf);
    for (let i = 1; i < arr.length; i++) {
      const gap = arr[i].x - Plan.right(arr[i - 1]);
      if (gap < -OVER) {
        overlaps.push({
          run, shelf, a: arr[i - 1].id, b: arr[i].id, by: +(-gap).toFixed(2),
        });
      }
    }
    if (arr.length) {
      const end = Plan.right(arr[arr.length - 1]);
      if (end > plan.runW + OVER) overrun.push({ run, shelf, end: +end.toFixed(2), by: +(end - plan.runW).toFixed(2) });
      if (arr[0].x < -OVER) overrun.push({ run, shelf, end: +arr[0].x.toFixed(2), by: +(-arr[0].x).toFixed(2), side: 'left' });
    }
    arr.forEach((it) => {
      if (!plan.fitsShelf(it, run, shelf)) {
        tooTall.push({ run, shelf, id: it.id, h: it.h, pitch: +plan.pitch(run, shelf).toFixed(2) });
      }
    });
  });
  plan.items.forEach((it) => {
    if (!(it.hf >= 1) || it.hf !== Math.round(it.hf)) badFacings.push({ id: it.id, hf: it.hf });
  });
  return { overlaps, overrun, tooTall, badFacings,
    /* Inches, not just incidents. The as-built plan carries a few large inherited
       overlaps — one SKU parked at x=0 on top of its neighbour — and a repack can
       legitimately re-attribute one of those to a different pair, which leaves the
       count flat while the geometry changed. Measuring the total tells the truth:
       a fix must never add overlap, and in practice these shrink. */
    overlapInches: +overlaps.reduce((s, o) => s + o.by, 0).toFixed(2),
    count: overlaps.length + overrun.length + tooTall.length + badFacings.length };
}

/* ── blocking ─────────────────────────────────────────────────────────────── */

/* A lane break: foreign facings sitting between two facings of the same node. */
function laneBreaks(plan, depth) {
  const out = [];
  plan.lanes().forEach(({ run, shelf }) => {
    const arr = plan.lane(run, shelf);
    const first = new Map(), last = new Map();
    arr.forEach((it, i) => {
      const k = plan.nodeKey(it, depth);
      if (!first.has(k)) first.set(k, i);
      last.set(k, i);
    });
    first.forEach((f, k) => {
      const l = last.get(k);
      let foreign = 0;
      for (let i = f; i <= l; i++) if (plan.nodeKey(arr[i], depth) !== k) foreign++;
      if (foreign) out.push({ depth, node: k, run, shelf, foreign });
    });
  });
  return out;
}

/* Stretches of a node across a run: merge its x-intervals from every lane it
   uses. Two stretches only count as separate if *foreign product actually sits
   in the gap between them* — otherwise the "gap" is nothing but the shelves
   being offset from each other by an inch or two, which is neither a defect nor
   something a shopper can see. Getting this wrong is how a fix that shifted a
   neighbour by 4 in looked like it had shattered somebody's block. */
const SAME_STRETCH = 12;            // below this, a gap is rounding, not a gap

function stretches(plan, depth) {
  const byNode = new Map();
  const byRun = new Map();
  plan.items.forEach((it) => {
    const k = plan.nodeKey(it, depth);
    if (!byNode.has(k)) byNode.set(k, new Map());
    const e = byNode.get(k);
    if (!e.has(it.run)) e.set(it.run, []);
    e.get(it.run).push(it);
    if (!byRun.has(it.run)) byRun.set(it.run, []);
    byRun.get(it.run).push(it);
  });

  const out = new Map();
  byNode.forEach((runs, k) => {
    const segs = [];
    [...runs.keys()].sort((a, b) => a - b).forEach((run) => {
      const mine = runs.get(run);
      const lanesUsed = new Set(mine.map((it) => it.shelf));
      const foreign = byRun.get(run).filter((it) => plan.nodeKey(it, depth) !== k && lanesUsed.has(it.shelf));
      const raw = mine.map((it) => ({ x0: it.x, x1: Plan.right(it) })).sort((a, b) => a.x0 - b.x0);
      let cur = null;
      raw.forEach((r) => {
        if (!cur) { cur = { run, x0: r.x0, x1: r.x1 }; segs.push(cur); return; }
        const gap = r.x0 - cur.x1;
        if (gap <= SAME_STRETCH) { cur.x1 = Math.max(cur.x1, r.x1); return; }
        // does anything else live in the gap, on a shelf this node uses?
        const intruder = foreign.some((f) => f.x < r.x0 - 0.5 && Plan.right(f) > cur.x1 + 0.5);
        if (!intruder) { cur.x1 = Math.max(cur.x1, r.x1); return; }
        cur = { run, x0: r.x0, x1: r.x1 }; segs.push(cur);
      });
    });
    out.set(k, segs);
  });
  return out;
}

function splitNodes(plan, depth) {
  const out = [];
  stretches(plan, depth).forEach((segs, k) => {
    if (segs.length > 1) out.push({ depth, node: k, blocks: segs.length });
  });
  return out;
}

/* ── sequence ─────────────────────────────────────────────────────────────── */

/* Read a node's own facings in its reading order and report inversions against
   the CDT key. Reading order is the same one the layout engine uses. */
function readingOrder(plan, items, orientation) {
  // exactly the order layoutBlock() lays a sequence down in, so "is it in order"
  // and "put it in order" can never disagree
  const ivs = orderIntervals(plan, intervals(plan, items.map((i) => i.id)), orientation);
  const out = [];
  ivs.forEach((iv) => iv.ids.slice().sort((a, b) => plan.get(a).x - plan.get(b).x)
    .forEach((id) => out.push(plan.get(id))));
  items.forEach((it) => { if (out.indexOf(it) < 0) out.push(it); });
  return out;
}

function sequenceBreaks(plan, ids, orientation) {
  const items = ids.map((id) => plan.get(id)).filter(Boolean);
  const read = readingOrder(plan, items, orientation);
  let bad = 0;
  for (let i = 1; i < read.length; i++) if (plan.cdtCompare(read[i - 1], read[i]) > 0) bad++;
  return { inversions: bad, order: read.map((r) => r.id) };
}

/* ── full audit + baseline diff ───────────────────────────────────────────── */

/* Lane breaks are checked at every level: a foreign facing wedged between two of
   a node's facings is wrong at any depth, and the check is invariant to how far
   things slide along a shelf.

   "One block across the run" is only checked at the levels the studio raises it
   at — subcategory and brand. Below those, the CDT splits vertically (Form,
   Handle), so a node deliberately owns different shelves, and its x-range per
   shelf differs by design; measuring a run-wide union there reports offsets as
   fractures and would veto correct fixes. */
const BREAK_DEPTHS = [1, 2, 3, 4];
const SPLIT_DEPTHS = [1, 2];
const DEPTHS = BREAK_DEPTHS;

function audit(plan) {
  const phys = physical(plan);
  const breaks = [], splits = [];
  BREAK_DEPTHS.forEach((d) => breaks.push(...laneBreaks(plan, d)));
  SPLIT_DEPTHS.forEach((d) => splits.push(...splitNodes(plan, d)));
  return {
    physical: phys,
    laneBreaks: breaks,
    splits,
    /* the vertical rule, counted: pairs where the higher facing is the bigger
       pack. `forced` are the ones the shelf openings make unavoidable. */
    grade: totalInversions(plan),
    key: {
      breaks: new Set(breaks.map((b) => [b.depth, b.node, b.run, b.shelf].join('#'))),
      splits: new Set(splits.map((s) => [s.depth, s.node].join('#'))),
    },
  };
}

/* What got worse. `introduced` is the only thing that can veto a fix. */
function diff(before, after) {
  const newBreaks = [...after.key.breaks].filter((k) => !before.key.breaks.has(k));
  const newSplits = [...after.key.splits].filter((k) => !before.key.splits.has(k));
  const fixedBreaks = [...before.key.breaks].filter((k) => !after.key.breaks.has(k));
  const fixedSplits = [...before.key.splits].filter((k) => !after.key.splits.has(k));
  return {
    newBreaks, newSplits, fixedBreaks, fixedSplits,
    newPhysical: after.physical.count - before.physical.count,
    overlapDelta: +(after.physical.overlapInches - before.physical.overlapInches).toFixed(2),
    introduced: newBreaks.length + newSplits.length,
    gradeFixed: before.grade.avoidable - after.grade.avoidable,
    gradeWorse: Math.max(0, after.grade.avoidable - before.grade.avoidable),
  };
}


/* ─── ops.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   ops.js — the two structural operations. Every defect in the studio resolves to
   one of them, or to nothing physical at all.

     relocate()    facings are in the wrong place: lift them, repair the holes
                   they leave, and splice them in at their CDT position inside
                   their own block. Serves "orphan" and "block is in N stretches".
     resequence()  the facings are in the right place but the wrong order: re-lay
                   the block's own footprint in CDT order. Serves size order, HE
                   order, flavour order and inversions.

   Both are built on layoutBlock(), which is the only thing that decides which
   SKU lands on which shelf. It escalates through four increasingly invasive
   strategies and reports which one it needed, so a fix is never silently more
   destructive than it looks.
   ──────────────────────────────────────────────────────────────────────────── */


const BAY = 121.92;
const bayOf = (x) => Math.floor(x / BAY) + 1;


/* ── partition an ordered sequence across an ordered set of intervals ───────
   Order-preserving: interval j gets a contiguous chunk of the sequence, so the
   sequence never doubles back and no sub-block can be interleaved with another.
   A chunk is admissible only if one facing of each of its SKUs fits the
   interval's width and every one of them clears the shelf pitch.

   Greedy left-to-right is not good enough: filling an early interval to the brim
   can strand a tall SKU on a short shelf later, or leave the tail nowhere to go.
   So this is a small feasibility DP, reconstructed forwards preferring the
   fullest chunk that still leaves a way out — and, among near-equally-full
   choices, the one that breaks at the shallowest CDT seam, so a chunk boundary
   lands between two sub-blocks rather than through the middle of one. */
function partition(plan, ivs, seq, opts) {
  const o = opts || {};
  const n = seq.length, m = ivs.length;
  const items = seq.map((id) => plan.get(id));
  const pre = [0];
  for (let i = 0; i < n; i++) pre.push(pre[i] + items[i].w);

  const tall = ivs.map((iv) => items.map((it) =>
    o.strictHeight === false ? true : plan.fitsShelf(it, iv.run, iv.shelf)));

  const chunkOK = (j, a, b) => {
    if (b === a) return true;
    if (pre[b] - pre[a] > ivs[j].cap + EPS) return false;
    for (let i = a; i < b; i++) if (!tall[j][i]) return false;
    return true;
  };

  const feas = [];
  for (let j = 0; j <= m; j++) feas.push(new Array(n + 1).fill(false));
  feas[m][n] = true;
  for (let j = m - 1; j >= 0; j--) {
    for (let a = n; a >= 0; a--) {
      for (let b = a; b <= n; b++) {
        if (!chunkOK(j, a, b)) break;
        if (feas[j + 1][b]) { feas[j][a] = true; break; }
      }
    }
  }
  if (!feas[0][0]) {
    // how much width a plain greedy fill would still be holding at the end —
    // a lower bound on what this footprint is short of
    let a = 0;
    for (let j = 0; j < m; j++) {
      let b = a;
      while (b < n && chunkOK(j, a, b + 1)) b++;
      a = b;
    }
    return { ok: false, slices: null, leftover: seq.slice(a),
      shortfall: +(pre[n] - pre[a]).toFixed(2) };
  }

  // seam depth between two consecutive SKUs: how much of the CDT path they share
  const seam = (i) => {
    if (i <= 0 || i >= n) return 0;
    const p = items[i - 1].path, q = items[i].path;
    let d = 0;
    while (d < p.length && String(p[d]) === String(q[d])) d++;
    return d;
  };

  const slices = [];
  let a = 0;
  for (let j = 0; j < m; j++) {
    const cands = [];
    for (let b = a; b <= n; b++) {
      if (!chunkOK(j, a, b)) break;
      if (feas[j + 1][b]) cands.push(b);
    }
    const fullest = cands.length ? cands[cands.length - 1] : a;
    const near = cands.filter((b) => pre[fullest] - pre[b] <= 24);   // within ~2 facings
    let pick = fullest;
    let bestSeam = Infinity;
    near.forEach((b) => { const s = seam(b); if (s < bestSeam) { bestSeam = s; pick = b; } });
    slices.push(seq.slice(a, pick));
    a = pick;
  }
  return { ok: true, slices, leftover: seq.slice(a) };
}

/* ── lay a sequence into a block, escalating only as far as needed ──────────
     block   the block's own footprint holds the sequence as it stands
     grown   the footprint is widened by taking facings off its lane neighbours
             (the licence the base plan has: spend facings, not listings)
     relaxed a pack ends up under a shelf tighter than it is — legal-ish, flagged
     lane    give up on moving SKUs between shelves and just reorder within each
             shelf; correct as far as it goes, and reported as partial
   Returns the mode it settled on so the caller can tell the truth about it. */
function layoutBlock(plan, ivsIn, seq, opts) {
  const o = opts || {};
  let ivs = ivsIn.slice();
  const notes = [];

  let part = partition(plan, ivs, seq, o);
  let mode = 'block';

  /* Tell the two kinds of failure apart before spending anything: if the
     sequence fits on width alone, the block is short of shelf *height*, and
     taking facings off the neighbours will not help. */
  const widthOnly = part.ok ? null : partition(plan, ivs, seq, Object.assign({}, o, { strictHeight: false }));
  const heightBound = !part.ok && widthOnly && widthOnly.ok;

  if (!part.ok && !heightBound && o.mayGrow !== false) {
    for (let iter = 0; iter < 8 && !part.ok; iter++) {
      const need = Math.max(part.shortfall, 1) + 0.5;
      // widen the interval whose lane can most easily give facings up: try from
      // the last in reading order backwards, since that is where the tail lands
      let done = false;
      for (let j = ivs.length - 1; j >= 0 && !done; j--) {
        const g = grow(plan, ivs[j], need, o);
        if (!g.ok) continue;
        notes.push(`widened run ${ivs[j].run} shelf ${ivs[j].shelf} by ${need.toFixed(1)}in`
          + ` — ${g.took.length} facing${g.took.length === 1 ? '' : 's'} reduced on neighbouring products`);
        ivs[j] = g.iv;
        done = true;
      }
      if (!done) break;
      ivs = orderIntervals(plan, ivs, o.orientation);
      part = partition(plan, ivs, seq, o);
      if (part.ok) mode = 'grown';
    }
  }

  if (!part.ok) {
    const relaxed = partition(plan, ivs, seq, Object.assign({}, o, { strictHeight: false }));
    if (relaxed.ok) {
      // only accept if it does not actually put a pack under too low a shelf
      const bad = [];
      relaxed.slices.forEach((sl, j) => sl.forEach((id) => {
        if (!plan.fitsShelf(plan.get(id), ivs[j].run, ivs[j].shelf)) {
          bad.push({ id, h: plan.get(id).h, shelf: ivs[j].shelf, run: ivs[j].run,
            pitch: +plan.pitch(ivs[j].run, ivs[j].shelf).toFixed(1) });
        }
      }));
      if (!bad.length) { part = relaxed; mode = 'block'; }
      else if (o.allowTooTall) {
        part = relaxed; mode = 'relaxed';
        bad.forEach((b) => notes.push(`${b.id} is ${b.h}in and shelf ${b.shelf} gives ${b.pitch}in`));
      } else {
        o._blockedBy = bad;
      }
    }
  }

  if (!part.ok) {
    // last resort: reorder inside each shelf only. Legal by construction — the
    // footprint per lane is untouched — but it cannot cure a cross-shelf order.
    const byIv = ivs.map((iv) => new Set(iv.ids));
    const slices = ivs.map((iv, j) => seq.filter((id) => byIv[j].has(id)));
    if (slices.reduce((s, sl) => s + sl.length, 0) === seq.length) {
      part = { ok: true, slices, leftover: [] };
      mode = 'lane';
      const blocked = (o._blockedBy || [])[0];
      notes.push('kept every SKU on its own shelf and reordered within each'
        + (blocked ? ` — ${blocked.id} is ${blocked.h}in and shelf ${blocked.shelf} gives ${blocked.pitch}in`
          : ' — the block cannot hold the full sequence across shelves'));
    }
  }

  if (!part.ok) return { ok: false, notes, reason: 'the sequence does not fit this block on any shelf arrangement' };

  ivs.forEach((iv, j) => {
    if (!part.slices[j].length) return;
    const r = repack(plan, iv, part.slices[j], o);
    if (!r.ok) throw new Error('repack overflow after partition — ' + JSON.stringify(r));
  });
  return { ok: true, mode, notes, ivs, slices: part.slices };
}

/* ── where a SKU belongs inside an existing block ──────────────────────────
   Never split a sub-block to insert something. Walk from the deepest CDT level
   up until the block actually contains SKUs sharing that prefix, then place the
   newcomer in CDT order inside that group. A baby liquid joins the liquids, not
   the sheets; a 2.7 oz powder joins the 2.7 oz powders. */
function insertionPoint(plan, seq, mover) {
  for (let d = plan.spine.length; d >= 1; d--) {
    const key = mover.path.slice(0, d).join(SEP);
    const group = [];
    seq.forEach((id, i) => { if (plan.get(id).path.slice(0, d).join(SEP) === key) group.push(i); });
    if (!group.length) continue;
    for (const i of group) if (plan.cdtCompare(mover, plan.get(seq[i])) < 0) return i;
    return group[group.length - 1] + 1;
  }
  return seq.length;
}

/* ── insert one SKU into a block, touching as little as possible ───────────
   A relocation is a location fix, not a licence to resequence someone else's
   block. So only the one stretch the newcomer lands in is re-laid: its existing
   SKUs keep their order, the newcomer is spliced in at its CDT slot inside them,
   and the width it needs is bought by taking facings off the *neighbouring*
   products on that shelf. Everything else on the plan is untouched, which is
   what makes "no new conflicts" true by construction rather than by luck. */
function insertLocal(plan, ivs, moverId, opts) {
  const o = opts || {};
  const mover = plan.get(moverId);
  const seq = [];
  ivs.forEach((iv) => iv.ids.slice().sort((a, b) => plan.get(a).x - plan.get(b).x)
    .forEach((id) => seq.push({ id, iv })));
  const at = insertionPoint(plan, seq.map((s) => s.id), mover);

  /* Candidate stretches, best first: the one the CDT slot falls in, then its
     neighbours in reading order, then anything else that clears the pack. */
  const home = at < seq.length ? seq[at].iv : (seq.length ? seq[seq.length - 1].iv : ivs[0]);
  const rest = ivs.filter((iv) => iv !== home)
    .sort((a, b) => Math.abs(ivs.indexOf(a) - ivs.indexOf(home)) - Math.abs(ivs.indexOf(b) - ivs.indexOf(home)));
  const cands = [home].concat(rest).filter((iv) => plan.fitsShelf(mover, iv.run, iv.shelf));
  if (!cands.length) return { ok: false, reason: `${moverId} (${mover.h}in) clears no shelf in the destination block` };

  const notes = [];
  for (const iv of cands) {
    const local = iv.ids.slice().sort((a, b) => plan.get(a).x - plan.get(b).x);
    local.splice(insertionPoint(plan, local, mover), 0, moverId);
    const need = local.reduce((s, id) => s + plan.get(id).w, 0) - iv.cap;
    let use = iv;
    if (need > EPS) {
      const g = grow(plan, iv, need + 0.5, o);
      if (!g.ok) continue;
      use = g.iv;
      notes.push(`made ${(need + 0.5).toFixed(1)}in at run ${iv.run} shelf ${iv.shelf} bay ${bayOf(iv.x0)}`
        + ` — ${g.took.length} facing${g.took.length === 1 ? '' : 's'} reduced on neighbouring products`);
    }
    const r = repack(plan, use, local, o);
    if (!r.ok) continue;
    if (iv !== home) notes.push(`landed on shelf ${iv.shelf} rather than ${home.shelf} — pack height`);
    return { ok: true, notes, iv: use, sequence: local };
  }
  return { ok: false, reason: `no stretch of the destination block can take ${moverId}`, notes };
}

/* ── the block, and the strays ─────────────────────────────────────────────
   A node "in N stretches" is resolved by picking the stretch worth keeping — the
   widest one — and moving everything else into it. Deriving this from the plan
   rather than trusting the pin's minor/major split matters: a node can be in
   three stretches, and moving only the one the pin named leaves it split. */
function stretchesOf(plan, ids) {
  const byRun = new Map();
  ids.forEach((id) => {
    const it = plan.get(id);
    if (!it) return;
    if (!byRun.has(it.run)) byRun.set(it.run, []);
    byRun.get(it.run).push(it);
  });
  const out = [];
  [...byRun.keys()].sort((a, b) => a - b).forEach((run) => {
    const arr = byRun.get(run).slice().sort((a, b) => a.x - b.x);
    let cur = null;
    arr.forEach((it) => {
      if (cur && it.x <= cur.x1 + ADJ) {
        cur.x1 = Math.max(cur.x1, Plan.right(it)); cur.ids.push(it.id);
      } else { cur = { run, x0: it.x, x1: Plan.right(it), ids: [it.id] }; out.push(cur); }
    });
  });
  out.forEach((s) => (s.span = s.x1 - s.x0));
  return out;
}

/* Walk up the SKU's own CDT path until a level has other members. A Powder SKU
   stranded in the detergent bays finds "Powder" this way even when nothing else
   shares its brand. */
function destinationFor(plan, moverIds, opts) {
  const o = opts || {};
  const movers = new Set(moverIds);
  const first = plan.get(moverIds[0]);
  const start = o.anchorNode ? o.anchorNode.split(SEP).length : plan.spine.length;
  for (let d = start; d >= 1; d--) {
    const key = first.path.slice(0, d).join(SEP);
    const sibs = plan.all().filter((it) => !movers.has(it.id) && it.path.slice(0, d).join(SEP) === key);
    if (sibs.length) return { depth: d, node: key, ids: sibs.map((s) => s.id) };
  }
  return null;
}



/* Reorder a block along each of its shelves, leaving every facing on the shelf it
   is already on. Each stretch keeps its own inches, so nothing outside the block
   moves. This is the whole job when the block's vertical grade is already right —
   which, in this plan, it usually is. */
function reorderWithinLanes(plan, ids, opts) {
  const o = opts || {};
  const byLane = new Map();
  intervals(plan, ids).forEach((iv) => {
    const k = iv.run + '|' + iv.shelf;
    if (!byLane.has(k)) byLane.set(k, []);
    byLane.get(k).push(iv);
  });
  const notes = [];
  byLane.forEach((lane) => {
    lane.sort((a, b) => a.x0 - b.x0);
    const mine = new Set();
    lane.forEach((iv) => iv.ids.forEach((id) => mine.add(id)));
    const seq = [...mine].sort((a, b) => plan.cdtCompare(plan.get(a), plan.get(b)) || a.localeCompare(b));
    const part = partition(plan, lane, seq, Object.assign({}, o, { strictHeight: false }));
    if (!part.ok) { notes.push(`shelf ${lane[0].shelf}: left as found`); return; }
    lane.forEach((iv, j) => {
      if (!part.slices[j].length) return;
      const r = repack(plan, iv, part.slices[j], o);
      if (!r.ok) throw new Error('repack overflow reordering within a shelf — ' + JSON.stringify(r));
    });
  });
  return { ok: true, notes };
}

/* ── the vertical gradient ─────────────────────────────────────────────────
   Re-hang one block so the big packs are low and the light ones are high, then
   run each shelf left-to-right in CDT order.

   Assignment is greedy from the bottom lane up, biggest pack first, which is the
   whole rule in one line. Two things make it safe rather than merely tidy:

     · each lane keeps exactly the inches it already owned, so the block's
       footprint is unchanged and no facing outside it ever moves;
     · a pack that will not fit under a lower shelf skips that lane and settles as
       low as the fixture allows, and is reported rather than forced.

   This is the one operation allowed to move a facing between shelves on account
   of size. Levels *below* size — HE, flavour — order along a shelf and have no
   say in which shelf anything is on. */
function gradeBlock(plan, ids, opts) {
  const o = opts || {};
  const live = ids.filter((id) => plan.get(id));
  if (live.length < 2) return { ok: false, reason: 'nothing to grade' };

  const was = inversions(plan, live);
  const snap0 = new Map(live.map((id) => [id, Object.assign({}, plan.get(id))]));

  /* If the block is already hung correctly — and in this plan most are — then the
     only thing to put right is the order along each shelf. Re-deriving the shelf
     assignment from scratch anyway is how a perfectly graded Tide block came back
     with two jumbos one shelf too high: a greedy refill has to make capacity
     choices the original planner already made better. Touch nothing that is not
     actually wrong. */
  if (was.avoidable === 0) {
    const flat = reorderWithinLanes(plan, live, o);
    return { ok: true, mode: 'inline', notes: flat.notes, pinned: [], restacked: 0,
      lanes: new Set(live.map((id) => plan.get(id).shelf)).size,
      inversions: inversions(plan, live) };
  }

  const ivs = intervals(plan, live);
  const lanes = new Map();
  ivs.forEach((iv) => {
    const k = iv.run + '|' + iv.shelf;
    if (!lanes.has(k)) lanes.set(k, { run: iv.run, shelf: iv.shelf, ivs: [], cap: 0 });
    const L = lanes.get(k);
    L.ivs.push(iv); L.cap += iv.cap;
  });
  const list = [...lanes.values()].sort((a, b) => a.run - b.run || bottomFirst(a, b));
  if (list.length < 2) return { ok: false, reason: 'block is on one shelf' };

  const before = live.map((id) => {
    const it = plan.get(id);
    return { id, run: it.run, shelf: it.shelf, x: it.x, hf: it.hf };
  });
  const vk = verticalKeys(plan, live);
  const heavyFirst = live.map((id) => plan.get(id))
    .sort((a, b) => (vk.get(b.id) - vk.get(a.id)) || plan.cdtCompare(b, a));

  const load = new Map(list.map((l) => [l, 0]));
  const pick = new Map(list.map((l) => [l, []]));
  const pinned = [];
  const home = new Map(live.map((id) => {
    const it = plan.get(id);
    return [id, list.find((l) => l.run === it.run && l.shelf === it.shelf) || list[0]];
  }));
  heavyFirst.forEach((it) => {
    const fits = list.filter((l) => plan.fitsShelf(it, l.run, l.shelf));
    let lane = fits.find((l) => load.get(l) + it.w <= l.cap + EPS)   // lowest shelf that fits
      || fits.slice().sort((a, b) => (b.cap - load.get(b)) - (a.cap - load.get(a)))[0]
      || home.get(it.id);                     // nothing clears it: leave it where it was
    /* A pack is never moved to a shelf it does not physically fit under. Doing
       that was turning a tidy gradient into thirteen packs jammed under shelves
       an inch too low — a worse plan than the inverted one it replaced. */
    if (!plan.fitsShelf(it, lane.run, lane.shelf)) lane = home.get(it.id);
    if (!plan.fitsShelf(it, list[0].run, list[0].shelf)) {
      pinned.push({ id: it.id, h: it.h, shelf: lane.shelf,
        lowest: list[0].shelf, pitch: +plan.pitch(list[0].run, list[0].shelf).toFixed(1) });
    }
    pick.get(lane).push(it.id);
    load.set(lane, load.get(lane) + it.w);
  });

  // each shelf, left to right, in CDT order across all of its own stretches
  const notes = [];
  list.forEach((l) => {
    const seq = pick.get(l).slice()
      .sort((a, b) => plan.cdtCompare(plan.get(a), plan.get(b)) || a.localeCompare(b));
    if (!seq.length) return;
    const stretches = l.ivs.slice().sort((a, b) => a.x0 - b.x0);
    const part = partition(plan, stretches, seq, Object.assign({}, o, { strictHeight: false }));
    if (!part.ok) { notes.push(`shelf ${l.shelf}: left as found, its stretches cannot hold the grade`); return; }
    stretches.forEach((iv, j) => {
      if (!part.slices[j].length) return;
      const r = repack(plan, iv, part.slices[j], o);
      if (!r.ok) throw new Error('repack overflow while grading — ' + JSON.stringify(r));
    });
  });

  const now = inversions(plan, live);
  if (now.avoidable > was.avoidable) {
    snap0.forEach((v, id) => Object.assign(plan.get(id), v));
    const flat = reorderWithinLanes(plan, live, o);
    return { ok: true, mode: 'inline', restacked: 0, pinned: [],
      notes: [`re-hanging this block would have made the gradient worse `
        + `(${was.avoidable} → ${now.avoidable}); reordered along each shelf instead`]
        .concat(flat.notes),
      lanes: list.length, inversions: inversions(plan, live) };
  }

  pinned.forEach((z) => notes.push(`${z.id} is ${z.h}in and shelf ${z.lowest} opens ${z.pitch}in`
    + ` — held on shelf ${z.shelf}, as low as the fixture allows`));

  const after = live.map((id) => {
    const it = plan.get(id);
    return { id, run: it.run, shelf: it.shelf, x: it.x, hf: it.hf };
  });
  const restacked = after.filter((z) => before.find((y) => y.id === z.id).shelf !== z.shelf).length;
  return { ok: true, notes, pinned, restacked, lanes: list.length, before, after,
    mode: restacked ? 'graded' : 'inline', inversions: now };
}

/* Every block the size level sits inside. Grading is a standing rule, not a
   per-defect one, so this runs over the whole plan. */
function gradeAll(plan, opts) {
  const out = [], skipped = [];
  blocksAt(plan).forEach((ids, node) => {
    const its = ids.map((id) => plan.get(id));
    if (new Set(its.map((i) => i.run + '|' + i.shelf)).size < 2) return;
    const was = inversions(plan, ids);
    if (was.avoidable === 0) return;

    /* Each block is graded on its own account and judged on its own account. One
       block that cannot be improved without breaking something must not drag the
       other thirty-eight down with it. */
    const before = audit(plan);
    const snap = new Map();
    ids.forEach((id) => snap.set(id, Object.assign({}, plan.get(id))));
    const r = gradeBlock(plan, ids, opts);
    const after = audit(plan);
    const d = diff(before, after);
    /* Strict improvement or nothing. Grading is greedy over a footprint that the
       grading itself changes, so "run it again and see" is not idempotent unless
       every pass has to leave the block measurably better than it found it. With
       this, a second press of Auto-resolve finds nothing to do. */
    const bad = after.physical.count > before.physical.count || d.introduced > 0
      || d.gradeWorse > 0 || !r.ok
      || r.inversions.avoidable >= was.avoidable;
    if (bad) {
      snap.forEach((v, id) => Object.assign(plan.get(id), v));
      skipped.push({ node, why: !r.ok ? r.reason
        : (after.physical.count > before.physical.count ? 'would not stay physically legal'
          : d.introduced > 0 ? 'would break a block'
            : `already as graded as its footprint allows (${was.avoidable} pair(s) left)`) });
      return;
    }
    out.push({ node, was, now: r.inversions, restacked: r.restacked,
      pinned: r.pinned, notes: r.notes });
  });
  return { graded: out, skipped };
}

/* ── relocate ─────────────────────────────────────────────────────────────── */
function relocate(plan, seedIds, opts) {
  const o = opts || {};
  const notes = [];
  const seeds = seedIds.filter((id) => plan.get(id));
  if (!seeds.length) return { ok: false, reason: 'nothing to move', notes };

  const snap = new Map();
  plan.items.forEach((v, k) => snap.set(k, Object.assign({}, v)));
  const rollback = () => snap.forEach((v, k) => Object.assign(plan.get(k), v));

  /* 1 — what is the block, and what actually has to move?
         If the caller named the node, take every facing of it and keep the
         widest stretch; otherwise the seeds are strays looking for a home. */
  let anchorIds, movers, destNode, destDepth;
  if (o.node) {
    const depth = o.node.split(SEP).length;
    const all = plan.all().filter((it) => it.path.slice(0, depth).join(SEP) === o.node).map((it) => it.id);
    const segs = stretchesOf(plan, all).sort((a, b) => b.span - a.span || b.ids.length - a.ids.length);
    if (!segs.length) return { ok: false, reason: 'node has no facings', notes };
    const keep = new Set(segs[0].ids);
    anchorIds = segs[0].ids.slice();
    movers = all.filter((id) => !keep.has(id));
    destNode = o.node; destDepth = depth;
    if (segs.length > 1) {
      notes.push(`${o.node} was in ${segs.length} stretches — keeping the widest `
        + `(run ${segs[0].run} bays ${bayOf(segs[0].x0)}–${bayOf(segs[0].x1 - 0.01)}, `
        + `${segs[0].span.toFixed(0)}in) and moving ${movers.length} facing${movers.length === 1 ? '' : 's'} into it`);
    }
  } else {
    const dest = destinationFor(plan, seeds, o);
    if (!dest) return { ok: false, reason: 'no destination block for these SKUs', notes };
    const segs = stretchesOf(plan, dest.ids).sort((a, b) => b.span - a.span || b.ids.length - a.ids.length);
    anchorIds = segs[0].ids.slice();
    movers = seeds.slice();
    destNode = dest.node; destDepth = dest.depth;
  }
  if (!movers.length) return { ok: false, reason: 'already contiguous — nothing to move', notes };
  if (!anchorIds.length) return { ok: false, reason: 'destination block is empty', notes };

  /* 2 — remember the vacated sites, then lift the strays off the plan */
  const holes = movers.map((id) => {
    const it = plan.get(id);
    return { run: it.run, shelf: it.shelf, x0: it.x, w: Plan.width(it) };
  }).sort((a, b) => a.run - b.run || a.shelf - b.shelf || a.x0 - b.x0);
  const merged = [];
  holes.forEach((h) => {
    const p = merged[merged.length - 1];
    if (p && p.run === h.run && p.shelf === h.shelf && h.x0 <= p.x0 + p.w + ADJ) {
      p.w = Math.max(p.w, h.x0 + h.w - p.x0);
    } else merged.push(Object.assign({}, h));
  });
  const parked = new Map();
  movers.forEach((id) => {
    const it = plan.get(id);
    parked.set(id, { run: it.run, shelf: it.shelf, x: it.x, hf: it.hf, bay: bayOf(it.x) });
    it.run = -1; it.shelf = -1; it.x = -1;
  });

  /* 3 — repair the vacated sites. The neighbours take the space as extra
         facings, so the blocking that was there stays solid rather than
         becoming a void that reads as a gap in the category. */
  merged.forEach((h) => {
    const r = fillHole(plan, h.run, h.shelf, h.x0, h.w, o);
    notes.push(`vacated ${h.w.toFixed(1)}in at run ${h.run} shelf ${h.shelf} bay ${bayOf(h.x0)}: `
      + `${r.absorbed.toFixed(1)}in taken up as extra facings on ${r.grown.length} neighbouring SKU`
      + `${r.grown.length === 1 ? '' : 's'}`
      + (r.residual > EPS ? `, ${r.residual.toFixed(1)}in spread across the block` : ''));
  });

  /* 4 — splice each stray into the block, one at a time and locally */
  const orient = plan.orient[plan.spine[Math.min(destDepth, plan.spine.length - 1)]] || 'horizontal';
  let ivs = orderIntervals(plan, intervals(plan, anchorIds), orient);
  notes.push(`destination: ${destNode}, run ${ivs[0].run} bays `
    + `${bayOf(Math.min(...ivs.map((i) => i.x0)))}–${bayOf(Math.max(...ivs.map((i) => i.x1)) - 0.01)}`);

  const placed = anchorIds.slice();
  for (const id of movers.slice().sort((a, b) => plan.cdtCompare(plan.get(a), plan.get(b)))) {
    const r = insertLocal(plan, ivs, id, Object.assign({}, o, { orientation: orient }));
    if (!r.ok) { rollback(); return { ok: false, reason: r.reason, notes: notes.concat(r.notes || []) }; }
    (r.notes || []).forEach((n) => notes.push(n));
    placed.push(id);
    ivs = orderIntervals(plan, intervals(plan, placed), orient);
  }

  /* Putting a SKU into a block is only half the job — the block it joined has to
     end up hung correctly, or the relocation leaves a jumbo above a small pack
     that the next fix then has to come back for. */
  const GD = Math.max(1, plan.spine.indexOf('__sizeLabel'));
  const touched = new Set(movers.map((id) => plan.get(id).path.slice(0, GD).join(SEP)));
  touched.forEach((key) => {
    /* Grade at the size level's own block — Subcategory / Brand / Form / Handle —
       not at whatever depth the destination happened to be found at. Grading a
       whole subcategory as one block re-hangs unrelated brands and pulls the SKU
       we just placed away from its own neighbours again. */
    const blockIds = plan.all().filter((z) => z.path.slice(0, GD).join(SEP) === key).map((z) => z.id);
    if (new Set(blockIds.map((id) => plan.get(id).run + '|' + plan.get(id).shelf)).size < 2) return;
    const was = inversions(plan, blockIds);
    const snap = new Map(blockIds.map((id) => [id, Object.assign({}, plan.get(id))]));
    const g = gradeBlock(plan, blockIds, o);
    if (!g.ok || g.inversions.avoidable > was.avoidable) {
      snap.forEach((v, id) => Object.assign(plan.get(id), v));
      return;
    }
    if (g.restacked) notes.push(`re-hung ${key}: ${g.restacked} facings changed shelf`
      + ' so the bigger packs sit lower');
  });

  return {
    ok: true, notes, mode: 'local', destination: destNode, moved: movers.slice(),
    to: movers.map((id) => {
      const it = plan.get(id);
      return { id, run: it.run, shelf: it.shelf, x: it.x, hf: it.hf, bay: bayOf(it.x) };
    }),
    from: movers.map((id) => Object.assign({ id }, parked.get(id))),
  };
}

/* ── resequence ───────────────────────────────────────────────────────────── */
function resequence(plan, ids, targetOrder, opts) {
  const o = opts || {};
  const notes = [];
  const live = ids.filter((id) => plan.get(id));
  if (live.length < 2) return { ok: false, reason: 'nothing to reorder', notes };

  const seq = (targetOrder && targetOrder.length
    ? targetOrder.filter((id) => plan.get(id))
    : live.slice().sort((a, b) => plan.cdtCompare(plan.get(a), plan.get(b))));
  live.forEach((id) => { if (seq.indexOf(id) < 0) seq.push(id); });

  const orient = o.orientation || 'horizontal';
  const before = live.map((id) => {
    const it = plan.get(id);
    return { id, run: it.run, shelf: it.shelf, x: it.x, hf: it.hf };
  });

  const ivs = orderIntervals(plan, intervals(plan, live), orient);

  /* ── which shelf a facing sits on is not this defect's business ───────────
     A horizontal level (Size, HE, Flavour) orders along x. The shelf a pack sits
     on was decided by the *vertical* levels above it — Form and Handle here — so a
     flavour fix reordering left-to-right has no authority to restack shelves.

     Letting it do so was the bug behind everything else: moving facings between
     shelves changed each block's per-lane balance, the width freed on one shelf
     got absorbed by a neighbouring block, and the next fix in the same branch
     found a narrower footprint and cut facings it did not need to. That is what
     made two fixes look like they were undoing each other, and what made the
     result depend on which pin you clicked first.

     So: horizontal levels are laid out lane by lane, each lane keeping exactly the
     facings and the inches it already had. Vertical levels are the ones allowed to
     move a facing between shelves, because that is precisely what they order. */
  let lay;
  if (orient === 'horizontal' && o.restack !== true) {
    /* Per shelf, not per stretch. A block can be broken into several stretches on
       one shelf by a foreign facing sitting in the middle of it; the order across
       that break still has to come out right, and putting it right is only a
       permutation along the same shelf, which is exactly what this level governs.
       Each stretch keeps its own inches, so nothing outside the block moves. */
    const byLane = new Map();
    ivs.forEach((iv) => {
      const k = iv.run + '|' + iv.shelf;
      if (!byLane.has(k)) byLane.set(k, []);
      byLane.get(k).push(iv);
    });
    const laneNotes = [];
    byLane.forEach((lane) => {
      lane.sort((a, b) => a.x0 - b.x0);
      const mine = new Set();
      lane.forEach((iv) => iv.ids.forEach((id) => mine.add(id)));
      const laneSeq = seq.filter((id) => mine.has(id));
      const part = partition(plan, lane, laneSeq, Object.assign({}, o, { strictHeight: false }));
      if (!part.ok) {                      // cannot happen on width alone, but be safe
        laneNotes.push(`shelf ${lane[0].shelf}: left as found, the stretches cannot hold the order`);
        return;
      }
      lane.forEach((iv, j) => {
        if (!part.slices[j].length) return;
        const r = repack(plan, iv, part.slices[j], o);
        if (!r.ok) throw new Error('repack overflow in shelf-locked resequence — ' + JSON.stringify(r));
      });
    });
    lay = { ok: true, mode: 'inline', notes: laneNotes, ivs };
  } else {
    lay = layoutBlock(plan, ivs, seq, Object.assign({}, o, { orientation: orient }));
  }
  if (!lay.ok) return { ok: false, reason: lay.reason, notes: notes.concat(lay.notes) };
  lay.notes.forEach((n) => notes.push(n));

  const after = live.map((id) => {
    const it = plan.get(id);
    return { id, run: it.run, shelf: it.shelf, x: it.x, hf: it.hf };
  });
  const shifted = after.filter((a) => {
    const b = before.find((z) => z.id === a.id);
    return b.shelf !== a.shelf || Math.abs(b.x - a.x) > 0.5 || b.hf !== a.hf;
  }).length;
  return { ok: true, notes, mode: lay.mode, sequence: seq, shifted, footprint: ivs.length, before, after };
}


/* ─── resolve.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   resolve.js — the entry point behind "Auto-resolve".

   autoResolve(plan, pin) does one defect. It:
     1. snapshots the plan and audits it, so "new conflict" has a meaning
     2. dispatches on the defect's kind to relocate() or resequence()
     3. re-audits and asks the only two questions that matter —
          is the defect the pin described actually gone?
          did anything that was legal before become illegal?
     4. if the answer to the second is yes, it tries a bounded repair pass, and
        if that fails it rolls the whole thing back and says so

   Nothing is ever marked resolved on the strength of having been attempted.
   ──────────────────────────────────────────────────────────────────────────── */


/* Which CDT level a pin's ordering rule lives at: the level immediately below
   the scope it was raised against. */
function levelFor(plan, pin) {
  const depth = pin.scope.split(SEP).filter((s) => s !== '*').length;
  return plan.spine[Math.min(depth, plan.spine.length - 1)];
}

function orientationFor(plan, pin) {
  return plan.orient[levelFor(plan, pin)] || 'horizontal';
}

/* The facings a SKU shares its deepest populated CDT ancestor with — the block
   it ought to be standing in. */
function kin(plan, it) {
  for (let d = plan.spine.length; d >= 1; d--) {
    const key = it.path.slice(0, d).join(SEP);
    const sibs = plan.all().filter((z) => z.id !== it.id && z.path.slice(0, d).join(SEP) === key);
    if (sibs.length) return sibs;
  }
  return [];
}

/* ── the order a sequence defect is asking for ─────────────────────────────
   Straight CDT order, from spine + vorder. The defect tells us *which block* is
   mis-sequenced; the tree already says what the right sequence is, tree-wide, and
   it is the same thing the app's own buildLayout() would produce. So there is
   nothing to reconcile and nothing for two defects to disagree about. */
function targetOrder(plan, pin) {
  return pin.ids.filter((id) => plan.get(id))
    .sort((a, b) => plan.cdtCompare(plan.get(a), plan.get(b)) || a.localeCompare(b));
}

/* Which defects carry a vertical claim. Size is the level the gradient lives at,
   and an "inversion" defect is a gradient complaint by definition — the studio's
   own wording for it is "Jumbos sit above small sizes". Everything below size in
   the spine (HE, flavour) orders along a shelf and has no say in which shelf
   anything is on. */
function gradesVertically(pin) {
  return pin.kind === 'size-order' || pin.kind === 'inversion';
}

/* ── did this pin's own defect go away? ───────────────────────────────────── */
function verdictFor(plan, pin, kind) {
  /* An orphan is not about stretch counts — a lone SKU is trivially one stretch,
     which is exactly why counting stretches said the stranded Powder facing was
     fine while it sat 18 bays away in the detergent. What matters is whether it
     is *touching its own kind*: the nearest facing of the deepest CDT ancestor
     that has other members. */
  if (kind === 'relocate' && pin.kind === 'orphan') {
    const worst = pin.ids.map((id) => {
      const it = plan.get(id);
      if (!it) return null;
      const family = new Set(kin(plan, it).map((f) => f.id));
      if (!family.size) return { id, ok: true, node: null };
      // is either facing immediately beside it one of its own? no tolerance needed
      const lane = plan.lane(it.run, it.shelf);
      const i = lane.findIndex((z) => z.id === id);
      const sides = [lane[i - 1], lane[i + 1]].filter(Boolean);
      const touching = sides.find((z) => family.has(z.id));
      let far = null, otherAisle = false;
      if (!touching) {
        const same = plan.all().filter((f) => family.has(f.id) && f.run === it.run);
        if (same.length) {
          far = Math.min(...same.map((f) => Math.max(it.x - Plan.right(f), f.x - Plan.right(it), 0)));
        } else otherAisle = true;              // its block is not even on this run
      }
      return { id, ok: !!touching, node: touching ? plan.nodeKey(touching, 2) : null,
        bays: far == null ? 0 : far / 121.92, otherAisle,
        neighbours: sides.map((z) => plan.nodeKey(z, 2)) };
    }).filter(Boolean).sort((a, b) => (a.ok ? 1 : 0) - (b.ok ? 1 : 0))[0];
    const ok = !worst || worst.ok;
    return {
      resolved: ok,
      detail: ok ? `sits against its own block${worst && worst.node ? ' (' + worst.node + ')' : ''}`
        : `${worst.otherAisle ? 'in a different aisle from its own block'
          : worst.bays.toFixed(1) + ' bays from its own block'}`
          + `; stands next to ${worst.neighbours.join(' and ') || 'nothing'}`,
      bays: worst ? +worst.bays.toFixed(2) : 0, otherAisle: !!(worst && worst.otherAisle),
    };
  }
  if (kind === 'relocate') {
    const node = pin.node || pin.scope;
    const depth = node.split(SEP).length;
    const segs = stretches(plan, depth).get(node) || [];
    const breaks = laneBreaks(plan, depth).filter((b) => b.node === node);
    return {
      resolved: segs.length <= 1 && !breaks.length,
      detail: `${node}: ${segs.length} stretch${segs.length === 1 ? '' : 'es'}, ${breaks.length} lane break(s)`,
      stretches: segs.length, laneBreaks: breaks.length,
    };
  }
  /* A horizontal level is satisfied when its facings read in CDT order along each
     shelf. Judging it on a single top-to-bottom ribbon across the whole block was
     the wrong yardstick — that measures the vertical levels' work, not this one's,
     and it is what pushed the layout into restacking shelves to satisfy it. */
  const orient = orientationFor(plan, pin);
  const live = pin.ids.filter((id) => plan.get(id));

  /* A size defect is two claims in one: the block should read small → large along
     each shelf, *and* the big packs should be at the bottom. Checking only the
     first is how a block full of jumbos on the top shelf got reported clean. */
  if (gradesVertically(pin)) {
    const inv = G.inversions(plan, live);
    const lanes = new Map();
    live.forEach((id) => {
      const it = plan.get(id);
      const k = it.run + '|' + it.shelf;
      if (!lanes.has(k)) lanes.set(k, []);
      lanes.get(k).push(it);
    });
    let along = 0, pairs = 0;
    lanes.forEach((arr) => {
      arr.sort((a, b) => a.x - b.x);
      for (let i = 1; i < arr.length; i++) { pairs++; if (plan.cdtCompare(arr[i - 1], arr[i]) > 0) along++; }
    });
    const ok = along === 0 && inv.avoidable === 0;
    const bits = [];
    if (along) bits.push(`${along} of ${pairs} adjacent pairs out of order along the shelf`);
    if (inv.avoidable) bits.push(`${inv.avoidable} pairs with the bigger pack above the smaller`);
    if (ok && inv.forced) bits.push(`${inv.forced} inversion(s) the shelf openings force`);
    return {
      resolved: ok,
      detail: ok ? `small → large along each shelf and down the bay`
        + (inv.forced ? ` (${inv.forced} pair(s) the fixture forces)` : '')
        : bits.join(' · '),
      along, inversions: inv.count, forced: inv.forced, avoidable: inv.avoidable,
    };
  }

  if (orient === 'horizontal') {
    const lanes = new Map();
    live.forEach((id) => {
      const it = plan.get(id);
      const k = it.run + '|' + it.shelf;
      if (!lanes.has(k)) lanes.set(k, []);
      lanes.get(k).push(it);
    });
    let inv = 0, pairs = 0;
    lanes.forEach((arr) => {
      arr.sort((a, b) => a.x - b.x);
      for (let i = 1; i < arr.length; i++) {
        pairs++;
        if (plan.cdtCompare(arr[i - 1], arr[i]) > 0) inv++;
      }
    });
    return {
      resolved: inv === 0,
      detail: inv === 0
        ? `in CDT order along ${lanes.size === 1 ? 'the shelf' : 'all ' + lanes.size + ' shelves'} it uses`
        : `${inv} of ${pairs} adjacent pairs still out of order along the shelf`,
      inversions: inv, pairs, lanes: lanes.size,
    };
  }

  const sb = sequenceBreaks(plan, pin.ids, orient);
  const want = targetOrder(plan, pin);
  const got = sb.order;
  const off = want.filter((id, i) => id !== got[i]).length;
  const exact = off === 0 && want.length === got.length;
  return {
    resolved: exact,
    detail: `reading order down the shelves ${exact ? 'matches the target exactly'
      : off + ' of ' + want.length + ' facings still out of place'}`,
    inversions: sb.inversions, misplaced: off, exact,
  };
}

/* ── repair pass ───────────────────────────────────────────────────────────
   The operations are written so they cannot break a block, but a lane can still
   end up with a void a shopper would read as a hole. This offers those to the
   neighbouring facings. It never moves a SKU between blocks, and never shifts
   anything outside the void's own two neighbouring stretches.

   The threshold is deliberately generous: below it, a gap is the residue of whole
   facings not dividing exactly into a shelf, which is normal and already present
   throughout the as-built plan. */
const VOID = 12;

function repair(plan, opts) {
  const fixed = [];
  plan.lanes().forEach(({ run, shelf }) => {
    for (let pass = 0; pass < 20; pass++) {
      const arr = plan.lane(run, shelf);
      let hit = null;
      for (let i = 1; i < arr.length; i++) {
        const gap = arr[i].x - Plan.right(arr[i - 1]);
        if (gap > VOID) { hit = { at: Plan.right(arr[i - 1]), gap }; break; }
      }
      if (!hit) break;
      const r = fillHole(plan, run, shelf, hit.at, hit.gap, opts);
      fixed.push({ run, shelf, gap: +hit.gap.toFixed(2), absorbed: r.absorbed, grown: r.grown });
      if (r.absorbed <= EPS) break;                          // nothing more to give
    }
  });
  return fixed;
}

/* ── one defect ───────────────────────────────────────────────────────────── */
function autoResolve(plan, pin, opts) {
  const o = opts || {};
  const before = audit(plan);
  const snapshot = plan.clone();

  /* Already clean? Then do nothing at all. Auto-resolve has to be safe to press
     twice — on a pin, on the whole plan — and the cheapest way to guarantee that
     is to check the finish line before starting. */
  const preKind = (pin.kind === 'split' || pin.kind === 'orphan') ? 'relocate'
    : ((pin.seqOrder && pin.seqOrder.length > 1) ? 'resequence' : null);
  let pre0 = null;
  if (preKind) {
    const pre = verdictFor(plan, pin, preKind);
    pre0 = pre;
    if (pre.resolved) {
      return { pin: pin.scope, kind: pin.kind, action: 'none', ok: true, resolved: true,
        physicalChange: false, alreadyClean: true, verdict: pre,
        reason: 'already satisfied — nothing to change', notes: [], diff: diff(before, before) };
    }
  }

  let kind = null, res = null;
  if (pin.kind === 'split') {
    // the node is in more than one stretch: keep the widest, move the rest in
    kind = 'relocate';
    res = relocate(plan, (pin.rot && pin.rot.minor) || pin.ids,
      Object.assign({}, o, { node: pin.node }));
  } else if (pin.kind === 'orphan') {
    // a facing sitting with the wrong neighbours: find the block it belongs to
    kind = 'relocate';
    res = relocate(plan, pin.ids, o);
  } else if (gradesVertically(pin)) {
    // re-hang the block: big packs low, light packs high, then CDT along each shelf
    kind = 'grade';
    res = gradeBlock(plan, pin.ids, o);
  } else if (pin.seqOrder && pin.seqOrder.length > 1) {
    kind = 'resequence';
    res = resequence(plan, pin.ids, targetOrder(plan, pin), Object.assign({}, o, { orientation: orientationFor(plan, pin) }));
  } else {
    return {
      pin: pin.scope, kind: pin.kind, action: 'none', ok: true, resolved: false,
      physicalChange: false,
      reason: 'this defect is a data / tree change (re-tag or re-cut) — nothing moves on the shelf until the tree is rebuilt',
      notes: [], diff: diff(before, before),
    };
  }

  if (!res.ok) {
    restore(plan, snapshot);
    return { pin: pin.scope, kind: pin.kind, action: kind, ok: false, resolved: false,
      reason: res.reason, notes: res.notes || [], diff: diff(before, audit(plan)) };
  }

  /* A void inside a block is not a rule violation but it is not a plan either.
     Close every one the neighbours can absorb, always — then audit. */
  const repaired = repair(plan, o);
  const after = audit(plan);
  const d = diff(before, after);

  const verdict = verdictFor(plan, pin, kind === 'grade' ? 'grade' : kind);
  const legal = after.physical.count <= before.physical.count
    && d.overlapDelta <= 0.5 && d.gradeWorse === 0;

  /* "lane" mode means the shelves themselves stop us finishing the job — a pack
     taller than the shelf the order wants it on. What it did is legal and is an
     improvement, so it is kept and reported as partial rather than thrown away:
     the planner needs to see the shelf-height constraint, not a dead button. */
  /* Three ways a fix can be honestly incomplete rather than wrong:
       · the shelves are too tight to finish the order (mode 'lane');
       · a pack is too tall to come down to where the gradient wants it;
       · the block's footprint cannot hold a perfect gradient — lane widths are
         fixed, so a few pairs can be left over even after a strict improvement.
     All three are kept and reported, because the plan is better than it was and
     the planner needs the number, not a dead button. */
  const partial = ((res.mode === 'lane') || kind === 'grade')
    && !verdict.resolved && legal && d.introduced === 0;

  /* A grade that cannot better what it found must change nothing at all. Without
     this the greedy would reshuffle a block it could not improve, and pressing
     Auto-resolve twice would give two different plans. */
  if (kind === 'grade' && pre0 && legal && d.introduced === 0
      && (verdict.avoidable || 0) >= (pre0.avoidable || 0)
      && (verdict.along || 0) >= (pre0.along || 0)) {
    restore(plan, snapshot);
    return {
      pin: pin.scope, kind: pin.kind, action: 'grade', ok: true, resolved: false,
      partial: true, physicalChange: false, verdict: pre0,
      reason: `already as graded as this block's shelf widths allow — `
        + `${pre0.avoidable} inverted pair(s) left, ${pre0.forced || 0} of them forced by the fixture`,
      notes: [], diff: diff(before, before),
    };
  }
  const ok = (verdict.resolved || partial) && legal && d.introduced === 0;

  if (!ok && o.rollbackOnFail !== false) {
    const failed = {
      pin: pin.scope, kind: pin.kind, action: kind, ok: false, resolved: false,
      reason: !verdict.resolved ? 'defect survived: ' + verdict.detail
        : (d.gradeWorse ? `${d.gradeWorse} new pair(s) with a bigger pack above a smaller one`
          : d.overlapDelta > 0.5 ? `${d.overlapDelta.toFixed(1)}in of new overlap`
            : !legal ? `${after.physical.count - before.physical.count} new physical violation(s)`
          : `${d.introduced} new blocking conflict(s): ` + d.newBreaks.concat(d.newSplits).slice(0, 4).join('; ')),
      notes: (res.notes || []).concat(repaired.length ? [`repair pass closed ${repaired.length} void(s)`] : []),
      diff: d, verdict,
    };
    restore(plan, snapshot);
    return failed;
  }

  return {
    pin: pin.scope, kind: pin.kind, action: kind, ok: true, resolved: verdict.resolved,
    physicalChange: true, partial: partial && !verdict.resolved, mode: res.mode,
    reason: (partial && !verdict.resolved)
      ? (kind === 'grade'
        ? `${d.gradeFixed} inverted pair(s) put right; ${verdict.avoidable} left that this `
          + 'block\'s shelf widths cannot absorb'
        : 'ordered as far as the shelves allow — ' + (res.notes || []).slice(-1)[0])
      : undefined,
    verdict, diff: d,
    notes: (res.notes || []).concat(repaired.length ? [`repair pass closed ${repaired.length} void(s)`] : []),
    moved: res.to || null, from: res.from || null, shifted: res.shifted,
  };
}

function restore(plan, snap) {
  plan.items.clear();
  snap.items.forEach((v, k) => plan.items.set(k, Object.assign({}, v)));
}

/* ── every defect, hardest first ───────────────────────────────────────────
   Location defects go before order defects: relocating changes a block's
   footprint, and resequencing is defined against whatever footprint it finds, so
   doing it the other way round would throw the ordering work away. */
const KIND_RANK = { orphan: 0, split: 1, inversion: 2, 'size-order': 3, 'he-order': 4, 'flavour-order': 5, ambiguous: 9 };

function autoResolveAll(plan, pins, opts) {
  const o = opts || {};
  const order = pins.slice().sort((a, b) =>
    (KIND_RANK[a.kind] == null ? 8 : KIND_RANK[a.kind]) - (KIND_RANK[b.kind] == null ? 8 : KIND_RANK[b.kind])
    || a.scope.localeCompare(b.scope));
  const results = [];

  /* Locations first — a stray has to be in its own block before the block can be
     hung properly. */
  order.filter((p) => p.kind === 'orphan' || p.kind === 'split')
    .forEach((p) => results.push(autoResolve(plan, p, o)));

  /* Then the vertical rule, over every block, whether a defect flagged it or not.
     Small and light up, big and heavy down is a standing guideline, not a
     per-defect one, and applying it only where a pin happens to point would leave
     most of the plan inverted. */
  const gr = o.grade === false ? { graded: [], skipped: [] } : gradeAll(plan, o);
  const graded = gr.graded;
  if (graded.length || gr.skipped.length) {
    const restacked = graded.reduce((s, g) => s + g.restacked, 0);
    const pinned = graded.reduce((s, g) => s + g.pinned.length, 0);
    results.push({
      pin: 'every block', kind: 'size gradient', action: 'grade', ok: true, resolved: true,
      physicalChange: true,
      verdict: { resolved: true, detail: `${graded.length} blocks re-hung, ${restacked} facings changed shelf` },
      notes: [`big packs to the bottom, light packs to the top, CDT order along each shelf`]
        .concat(pinned ? [`${pinned} pack(s) held high — too tall for the shelf below`] : [])
        .concat(graded.slice().sort((a, b) => b.was.count - a.was.count).slice(0, 6)
          .map((g) => `${g.node}: ${g.was.count} → ${g.now.count} inverted pairs`)),
      graded,
      /* Blocks this pass deliberately left alone, each with the reason. These are
         the exceptions the planner has to see — a block that cannot be improved is
         a finding, not a silence. */
      skipped: gr.skipped,
      pinned: graded.reduce((s, g) => s.concat(g.pinned.map((z) => Object.assign({ node: g.node }, z))), []),
    });
  }

  order.filter((p) => p.kind !== 'orphan' && p.kind !== 'split')
    .forEach((p) => results.push(autoResolve(plan, p, o)));
  return results;
}


/* ─── index.js ─────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────────────
   index.js — the surface the studio calls.

   The existing UI already has the right shape for this: resolveFix(pin, cur)
   returns a map of id → {run, shelf, x}, composed in order. This exposes the
   same contract, with hf added (facings are how space gets made, so they have to
   come back too) and with a report attached so the fix panel can say what
   happened instead of asserting that something did.

     const studio = attach(CANVAS, ITEMS);      // once, after the JSON loads
     const step   = studio.resolve(pin);        // one Auto-resolve click
     step.newPos                                // { id: {run, shelf, x, hf} }
     step.resolved / step.partial / step.reason
     studio.resolveAll()                        // the "Resolve all" button

   `resolve` is cumulative: each call works on the positions the previous calls
   produced, which is what "each fix is resolved against the plan as it now
   stands" means. `preview(pin)` does the same without committing.
   ──────────────────────────────────────────────────────────────────────────── */


function attach(canvas, itemsDoc, opts) {
  const base = loadPlan(canvas, itemsDoc, opts);
  const pins = canvas.pins || [];
  let plan = base.clone();

  const positions = (p) => {
    const out = {};
    p.items.forEach((it) => {
      const b = base.get(it.id);
      if (b.run === it.run && b.shelf === it.shelf
        && Math.abs(b.x - it.x) < 0.05 && b.hf === it.hf) return;
      out[it.id] = { run: it.run, shelf: it.shelf, x: it.x, hf: it.hf };
    });
    return out;
  };

  const wrap = (p, r) => Object.assign({}, r, {
    newPos: positions(p),
    audit: summarise(audit(p)),
  });

  return {
    get plan() { return plan; },
    base,
    pins,

    /* what the plan looks like right now, in the studio's own terms */
    positions: () => positions(plan),
    audit: () => summarise(audit(plan)),

    /* is this pin currently satisfied? drives the pin's colour without applying
       anything */
    check: (pin) => verdictFor(plan, pin,
      (pin.kind === 'split' || pin.kind === 'orphan') ? 'relocate' : 'resequence'),

    /* one Auto-resolve click */
    resolve: (pin, opts) => {
      const r = autoResolve(plan, pin, opts);
      return wrap(plan, r);
    },

    /* the same, without committing — for a hover preview or a diff panel */
    preview: (pin, opts) => {
      const p = plan.clone();
      const r = autoResolve(p, pin, opts);
      return wrap(p, r);
    },

    /* the "Resolve all" button */
    resolveAll: (opts) => {
      const rs = autoResolveAll(plan, pins, opts);
      return { steps: rs, newPos: positions(plan), audit: summarise(audit(plan)) };
    },

    reset: () => { plan = base.clone(); },
  };
}

function summarise(a) {
  return {
    inverted: a.grade.count,
    invertedAvoidable: a.grade.avoidable,
    overlaps: a.physical.overlaps.length,
    overruns: a.physical.overrun.length,
    tooTall: a.physical.tooTall.length,
    laneBreaks: a.laneBreaks.length,
    splitNodes: a.splits.length,
    clean: a.physical.count === 0,
  };
}


  const A = { audit, diff, physical, laneBreaks, splitNodes, stretches,
    sequenceBreaks, readingOrder, inversions, BREAK_DEPTHS, SPLIT_DEPTHS };
  const G = { verticalKeys, blocksAt, inversions, totalInversions };
  root.CDTResolve = {
    attach, loadPlan, Plan,
    audit: A,
    ops: { relocate, resequence, layoutBlock, partition, insertionPoint, insertLocal,
           destinationFor, stretchesOf },
    space: { intervals, orderIntervals, fitFacings, repack, closeUp, fillHole, grow },
    grade: G,
    autoResolve, autoResolveAll, verdictFor, targetOrder, gradesVertically,
  };
})(typeof window !== 'undefined' ? window : globalThis);
